/**
 * AppDownloadPage — public (no auth) staff-app download page at /app.
 * Mobile-first: staff open this on their phones to install the Android APK.
 * Theme-aware (follows the data-theme bootstrap like every public page).
 * The APK bytes are served by the web container's nginx from a host
 * bind-mount at /downloads/ — not baked into the image.
 */

import { useState } from 'react';
import { Download, ShieldCheck, AlertTriangle, Smartphone, Copy, Check } from 'lucide-react';

const APK = {
  version: 'v1.0.8',
  build: 10,
  date: 'Oct 19, 2026',
  sizeMB: '89.9 MB',
  sha256: 'd745e32fe88d9ec1631f678fbd8c22bcbc581006ca4bd63f5e448c7818f1046e',
  url: '/downloads/1financial-staff.apk',
};

export function AppDownloadPage() {
  const [copied, setCopied] = useState(false);

  async function copySha() {
    try {
      await navigator.clipboard.writeText(APK.sha256);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — ignore */ }
  }

  return (
    <div className="min-h-screen bg-bg-base flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center font-bold text-xl text-accent-fg shadow-panel">1F</div>
          <h1 className="text-xl font-semibold text-white mt-4">1Financial Staff</h1>
          <p className="text-sm text-muted mt-1">The OPS companion app for Android — my work, time and attendance on the go.</p>
        </div>

        <div className="bg-bg-panel border border-border rounded-xl p-5 space-y-4">
          <a
            href={APK.url}
            className="flex items-center justify-center gap-2 w-full bg-accent hover:opacity-90 text-accent-fg font-semibold text-base px-4 py-3 rounded-xl"
          >
            <Download size={19} /> Download for Android
          </a>
          <div className="flex items-center justify-between text-xs text-muted">
            <span>Version {APK.version} (build {APK.build})</span>
            <span>{APK.date}</span>
            <span>{APK.sizeMB}</span>
          </div>
          <div className="bg-bg-card border border-border rounded-lg p-3">
            <p className="text-[11px] text-muted mb-1 flex items-center gap-1"><ShieldCheck size={12} /> SHA-256 (verify after download)</p>
            <div className="flex items-center gap-2">
              <code className="text-[10px] text-white break-all leading-4 flex-1">{APK.sha256}</code>
              <button onClick={copySha} aria-label="Copy SHA-256" className="text-muted hover:text-white flex-shrink-0 p-1">
                {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-bg-panel border border-border rounded-xl p-5">
          <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Smartphone size={15} className="text-muted" /> Install instructions</h2>
          <ol className="space-y-2.5 text-sm text-muted list-decimal list-inside">
            <li>Tap <span className="text-white">Download for Android</span> above.</li>
            <li className="!text-amber-300">
              <span className="inline-flex items-center gap-1"><AlertTriangle size={13} /></span>
              Upgrading from v1.0.4 or older? <span className="text-white">Uninstall the old app first</span> — the signing key changed, Android will refuse an in-place upgrade. Your account lives on the server, nothing is lost.
            </li>
            <li>Open the downloaded file. When prompted, allow <span className="text-white">"Install unknown apps"</span> for your browser (one time).</li>
            <li>Open <span className="text-white">1Financial Staff</span> and sign in with your OPS email and password.</li>
          </ol>
        </div>

        <div className="bg-bg-panel border border-border rounded-xl p-5">
          <h2 className="text-sm font-medium text-white mb-2">On iPhone?</h2>
          <p className="text-sm text-muted">
            There's no iOS app yet — use the web app instead: open <span className="text-white">ops.1financial.net</span> in Safari, tap
            <span className="text-white"> Share → Add to Home Screen</span>. It installs as an app icon and works fullscreen.
          </p>
        </div>

        <p className="text-center text-[11px] text-muted">
          Having trouble? Contact your manager. · <a href="/login" className="text-accent hover:underline">Sign in to OPS</a>
        </p>
      </div>
    </div>
  );
}
